import React, { useEffect, useState } from 'react'
import {
    DialogActions,
    DialogTitle,
    DialogContent,
    Button,
    Box,
    Divider,
    Typography
} from '@material-ui/core'
import { useSelector } from 'react-redux'
import {
    InputField,
    DropdownField,
    CheckboxField
} from 'components/forms/reduxForm'
import {
    phoneNumberValidation,
    validateEmail,
    maxLength20
} from 'tools/forms/validators'
import { Dialog } from 'components/shared/Dialog'
import { PasswordValidator } from 'components/PasswordValidation/PasswordValidator'
import strings from 'strings'
import AuthService from 'tools/AuthService'

const passwordValidation = (values, allValues) => {
    const newPassword = allValues['newPassword']
    if (newPassword === values) {
        return undefined
    } else {
        return 'Passwords dont match'
    }
}

const UserForm = (props) => {
    const {
        open,
        onClose,
        onSubmit,
        mode = 'Create',
        submitting,
        pristine,
        loading,
        customerData,
        initialValues
    } = props
    const [openTooltip, setOpenTooltip] = useState(false)
    const [valid, setValid] = useState(false)
    const gs = useSelector(state => state)
    const { newPassword, roles: selectedRoles } = gs.form?.profileForm?.values || {}
    // Sales Rep is never picked here: reps are created from Settings > Sales Representative, and their role is locked
    // Read from the form's initial state: the initialValues prop here is not the edited user
    const salesRepLocked = gs.form?.profileForm?.initial?.roles?.[0]?.systemName === 'SALES_REP'
    const userRoles = gs.root.rolesList
        .filter(data => salesRepLocked || data.systemName !== 'SALES_REP')
        .map(data => ({ label: data.name, value: data.roleId, systemName: data.systemName }))
    // The approver role is granted on its own (it sits on top of the main role), and only to existing users.
    const creditMemoApprovalEnabled = customerData?.featureFlags
        ? customerData.featureFlags.includes('CREDIT_MEMO_APPROVAL')
        : AuthService.getClientFeatures().creditMemoApprovalEnabled
    // Sales reps only see the applications shared with them, so they can't approve credit memos
    const isSalesRepRole = selectedRoles?.[0]?.systemName === 'SALES_REP'
    const showApproverToggle = mode === 'Edit' && creditMemoApprovalEnabled && !isSalesRepRole
    // Customer Admins approve through their role; the add-on toggle can't take that away.
    const approverByRole = !!initialValues?.creditMemoApproverByRole

    useEffect(() => {
        if (mode !== 'Create')
            setValid(true)
    }, [mode]);

    return (
        <Dialog
            open={open}
            fullWidth={true}
            maxWidth='sm'
        >
            <DialogTitle>
                {mode} {strings.USER}
            </DialogTitle>
            <DialogContent>
                <InputField
                    name='firstName'
                    label='First Name *'
                    required
                />
                <InputField
                    name='lastName'
                    label='Last Name *'
                    required
                />
                <DropdownField
                    name="roles[0]"
                    label="Role"
                    options={userRoles}
                    getFullValue
                    disabled={salesRepLocked}
                    required
                />
                <InputField
                    name='phone'
                    label='Phone'
                    validate={phoneNumberValidation}
                    isphoneNumber
                />
                <InputField
                    name='email'
                    label='Email *'
                    validate={validateEmail}
                    required
                    // A Sales Rep login is linked to its sales representative by email: it comes from there
                    disabled={mode === 'Edit' || salesRepLocked}
                />
                {
                    mode === strings.CREATE && (
                        <>
                            <PasswordValidator password={newPassword} isValid={setValid} open={openTooltip} >
                                <InputField
                                    name='newPassword'
                                    type='password'
                                    label='New Password *'
                                    validate={maxLength20}
                                    required
                                    onFocus={() => setOpenTooltip(true)}
                                    onBlur={() => setOpenTooltip(false)}
                                />
                            </PasswordValidator>
                            <InputField
                                name='password'
                                type='password'
                                label='Confirm Password *'
                                validate={passwordValidation}
                                required
                            />
                        </>
                    )
                }
                {showApproverToggle && (
                    <Box mt={3}>
                        <Divider />
                        <Typography variant='subtitle2' style={{ marginTop: 16, fontWeight: 600 }}>
                            {strings.APPLICANT.CREDIT_MEMO.APPROVAL.USER_PERMISSIONS}
                        </Typography>
                        <CheckboxField
                            name='creditMemoApprover'
                            label={strings.APPLICANT.CREDIT_MEMO.APPROVAL.USER_APPROVER}
                            disabled={approverByRole}
                        />
                        <Typography variant='body2' color='textSecondary' style={{ marginLeft: 32, marginTop: -4 }}>
                            {approverByRole
                                ? strings.APPLICANT.CREDIT_MEMO.APPROVAL.USER_APPROVER_BY_ROLE_HELP
                                : strings.APPLICANT.CREDIT_MEMO.APPROVAL.USER_APPROVER_HELP}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button color='primary' onClick={onClose}>
                    {strings.CANCEL}
                </Button>
                <Button
                    variant="contained"
                    color='primary'
                    onClick={valid ? onSubmit : null}
                    disabled={loading || submitting || pristine || !valid}
                >
                    {mode} {strings.USER}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default UserForm